// src/components/social/EventCard.jsx
// Evenements Fynho Social : liste paginee, creation, chat de groupe (texte seul), gestion admin

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import * as socialApi from '../../utils/socialApi';

const CATEGORIES = [
  { value: 'voyage', label: 'Voyage', icon: '✈️' },
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'sortie', label: 'Sortie', icon: '🎉' },
  { value: 'sport', label: 'Sport', icon: '⚽' },
  { value: 'business', label: 'Business', icon: '💼' },
  { value: 'logement', label: 'Logement', icon: '🏠' },
  { value: 'autre', label: 'Autre', icon: '📌' },
];

const PAGE_SIZE = 7;

const emptyForm = {
  title: '', type: 'public', secret_code: '', district: '', event_date: '',
  event_time: '', max_participants: '', category: 'autre', description: '', image: '',
};

const EventChatWindow = ({ eventId, eventTitle, userId, onClose }) => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await socialApi.getEventMessages(eventId, 0);
      setMessages(res.messages || []);
    } catch (err) {
      // silencieux
    }
  }, [eventId]);

  useEffect(() => {
    loadMessages();
    pollRef.current = setInterval(loadMessages, 3000);
    return () => clearInterval(pollRef.current);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await socialApi.sendEventMessage(eventId, userId, text.trim(), null);
      setText('');
      loadMessages();
    } catch (err) {
      alert(err.message || 'Message non envoye.');
    } finally {
      setSending(false);
    }
  };

  const s = {
    overlay: { position: 'fixed', inset: 0, backgroundColor: theme.bg, zIndex: 2500, display: 'flex', flexDirection: 'column' },
    header: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: `1px solid ${theme.borderSoft}` },
    backBtn: { background: 'none', border: 'none', fontSize: '20px', color: theme.textPrimary, cursor: 'pointer', padding: '4px 6px' },
    title: { flex: 1, fontSize: '15px', fontWeight: '700', color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    messagesArea: { flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' },
    messageRow: { display: 'flex' },
    bubble: { maxWidth: '78%', padding: '9px 12px', borderRadius: '14px', fontSize: '14px' },
    bubbleMine: { backgroundColor: theme.gold, color: '#FFFFFF' },
    bubbleTheirs: { backgroundColor: theme.bgCard, color: theme.textPrimary, border: `1px solid ${theme.border}` },
    bubbleAuthor: { fontSize: '11px', fontWeight: '700', marginBottom: '2px', opacity: 0.8 },
    inputRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', borderTop: `1px solid ${theme.borderSoft}` },
    textInput: { flex: 1, padding: '10px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '20px', color: theme.textPrimary, fontSize: '14px', outline: 'none' },
    sendBtn: { width: '38px', height: '38px', borderRadius: '50%', backgroundColor: theme.gold, color: '#FFFFFF', border: 'none', fontSize: '15px', cursor: 'pointer' },
  };

  return (
    <div style={s.overlay}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={onClose}>←</button>
        <div style={s.title}>🎉 {eventTitle}</div>
      </div>
      <div style={s.messagesArea}>
        {messages.map((msg) => {
          const isMine = String(msg.sender_id) === String(userId);
          return (
            <div key={msg.id} style={{ ...s.messageRow, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
              <div style={{ ...s.bubble, ...(isMine ? s.bubbleMine : s.bubbleTheirs) }}>
                {!isMine && <div style={s.bubbleAuthor}>{msg.username}</div>}
                {msg.content}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={s.inputRow}>
        <input
          style={s.textInput}
          type="text"
          placeholder="Ecrire au groupe..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
        />
        <button style={s.sendBtn} onClick={handleSend} disabled={sending}>➤</button>
      </div>
    </div>
  );
};

const EventCard = ({ userId, city, isAdmin, isAuthenticated, onRequireLogin }) => {
  const { theme } = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openChatEvent, setOpenChatEvent] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2500);
  };

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await socialApi.listEvents(city, userId);
      setEvents(res.events || []);
    } catch (err) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [city, userId]);

  useEffect(() => { loadEvents(); setCurrentPage(1); }, [loadEvents]);

  const handleFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleOpenCreateForm = () => {
    if (!isAuthenticated) return onRequireLogin();
    setShowCreateForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!form.title.trim() || !form.event_date) {
      setCreateError('Titre et date sont requis.');
      return;
    }
    setCreating(true);
    try {
      await socialApi.createEvent({
        creator_id: userId,
        title: form.title.trim(),
        type: form.type,
        secret_code: form.type === 'private' ? form.secret_code : undefined,
        city,
        district: form.district.trim() || null,
        event_date: form.event_date,
        event_time: form.event_time || null,
        max_participants: form.max_participants ? parseInt(form.max_participants, 10) : null,
        category: form.category,
        description: form.description.trim() || null,
        image: form.image.trim() || null,
      });
      setForm(emptyForm);
      setShowCreateForm(false);
      loadEvents();
    } catch (err) {
      setCreateError(err.message || 'Erreur lors de la creation.');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (event) => {
    if (!isAuthenticated) return onRequireLogin();
    let secretCode;
    if (event.type === 'private') {
      secretCode = window.prompt('Code secret de cet evenement :');
      if (!secretCode) return;
    }
    // Mise a jour optimiste : le bouton devient "Quitter" immediatement
    setEvents((prev) => prev.map((e) => (
      e.id === event.id
        ? { ...e, is_member: true, participant_count: (parseInt(e.participant_count, 10) || 0) + 1 }
        : e
    )));
    try {
      await socialApi.joinEvent(event.id, userId, secretCode);
      showToast(`✓ Tu as rejoint "${event.title}" !`);
    } catch (err) {
      // Echec reel -> on annule la mise a jour optimiste
      setEvents((prev) => prev.map((e) => (
        e.id === event.id
          ? { ...e, is_member: false, participant_count: Math.max(0, (parseInt(e.participant_count, 10) || 1) - 1) }
          : e
      )));
      alert(err.message || 'Impossible de rejoindre cet evenement.');
    }
  };

  const handleLeave = async (event) => {
    if (!isAuthenticated) return onRequireLogin();
    setEvents((prev) => prev.map((e) => (
      e.id === event.id
        ? { ...e, is_member: false, participant_count: Math.max(0, (parseInt(e.participant_count, 10) || 1) - 1) }
        : e
    )));
    showToast(`Tu as quitte "${event.title}".`);
    try {
      await socialApi.leaveEvent(event.id, userId);
    } catch (err) {
      loadEvents();
    }
  };

  const handleCancel = async (event) => {
    if (!window.confirm(`Annuler l'evenement "${event.title}" ?`)) return;
    await socialApi.cancelEvent(event.id, userId);
    loadEvents();
  };

  const handleAdminDelete = async (event) => {
    if (!window.confirm(`[Admin] Supprimer definitivement "${event.title}" ?`)) return;
    try {
      await socialApi.adminDeleteEvent(event.id);
      loadEvents();
    } catch (err) {
      alert(err.message || "Erreur lors de la suppression admin.");
    }
  };

  const handleShare = async (event) => {
    const link = `${window.location.origin}/social?event=${event.share_slug}`;
    const shareData = { title: event.title, text: `Rejoins "${event.title}" sur Fynho Social`, url: link };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) { /* annule */ }
    } else {
      try {
        await navigator.clipboard.writeText(link);
        alert('Lien copie !');
      } catch (err) {
        window.prompt('Copie ce lien :', link);
      }
    }
  };

  const handleOpenEvent = (event) => {
    if (!isAuthenticated) return onRequireLogin();
    if (!event.is_member) return;
    setOpenChatEvent({ id: event.id, title: event.title });
  };

  const totalPages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const pageEvents = events.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '12px' },
    createBtn: { padding: '12px', backgroundColor: theme.gold, border: 'none', borderRadius: '10px', color: '#FFFFFF', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
    formCard: { display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '12px' },
    input: { padding: '10px 12px', backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.textPrimary, fontSize: '14px', outline: 'none' },
    row: { display: 'flex', gap: '8px' },
    typeToggle: { display: 'flex', gap: '8px' },
    typeChip: (active) => ({ padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', backgroundColor: active ? theme.gold : theme.bgInput, color: active ? '#FFFFFF' : theme.textSecondary, border: `1px solid ${active ? theme.gold : theme.border}` }),
    errorText: { fontSize: '12px', color: '#F56565' },
    submitBtn: { padding: '10px', backgroundColor: theme.gold, border: 'none', borderRadius: '8px', color: '#FFFFFF', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    eventCard: { padding: '14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '6px', cursor: 'pointer' },
    eventHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    eventTitle: { fontSize: '15px', fontWeight: '700', color: theme.textPrimary },
    badge: (isPrivate) => ({ fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', backgroundColor: isPrivate ? 'rgba(245,101,101,0.15)' : 'rgba(72,187,120,0.15)', color: isPrivate ? '#F56565' : '#48BB78' }),
    eventMeta: { fontSize: '12px', color: theme.textSecondary },
    eventDesc: { fontSize: '13px', color: theme.textPrimary, marginTop: '2px' },
    memberHint: { fontSize: '11px', color: theme.gold, fontWeight: '700' },
    actionsRow: { display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' },
    actionBtn: { padding: '7px 12px', fontSize: '12px', fontWeight: '700', borderRadius: '8px', border: 'none', cursor: 'pointer' },
    emptyState: { textAlign: 'center', padding: '32px', color: theme.textSecondary, fontSize: '13px' },
    toast: { padding: '10px 14px', backgroundColor: 'rgba(72,187,120,0.12)', border: '1px solid rgba(72,187,120,0.35)', borderRadius: '10px', color: '#48BB78', fontSize: '13px', fontWeight: '600', textAlign: 'center' },
    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '6px', flexWrap: 'wrap' },
    pageBtn: (active) => ({ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${active ? theme.gold : theme.border}`, backgroundColor: active ? theme.gold : theme.bgCard, color: active ? '#FFFFFF' : theme.textSecondary, fontSize: '13px', fontWeight: '700', cursor: 'pointer' }),
    pageArrow: { padding: '6px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`, backgroundColor: theme.bgCard, color: theme.textSecondary, fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  };

  return (
    <div style={styles.wrap}>
      {!showCreateForm && (
        <button style={styles.createBtn} onClick={handleOpenCreateForm}>
          {isAuthenticated ? '+ Creer un evenement' : '🔒 Se connecter pour creer un evenement'}
        </button>
      )}

      {showCreateForm && (
        <form style={styles.formCard} onSubmit={handleCreate}>
          <input style={styles.input} placeholder="Titre (ex: Voyage a Kribi)" value={form.title} onChange={(e) => handleFormChange('title', e.target.value)} required />

          <div style={styles.typeToggle}>
            <div style={styles.typeChip(form.type === 'public')} onClick={() => handleFormChange('type', 'public')}>Public</div>
            <div style={styles.typeChip(form.type === 'private')} onClick={() => handleFormChange('type', 'private')}>Prive</div>
          </div>
          {form.type === 'private' && (
            <input style={styles.input} placeholder="Code secret" value={form.secret_code} onChange={(e) => handleFormChange('secret_code', e.target.value)} />
          )}

          <input style={styles.input} placeholder="Quartier / lieu" value={form.district} onChange={(e) => handleFormChange('district', e.target.value)} />

          <div style={styles.row}>
            <input style={{ ...styles.input, flex: 1 }} type="date" value={form.event_date} onChange={(e) => handleFormChange('event_date', e.target.value)} required />
            <input style={{ ...styles.input, flex: 1 }} type="time" value={form.event_time} onChange={(e) => handleFormChange('event_time', e.target.value)} />
          </div>

          <div style={styles.row}>
            <select style={{ ...styles.input, flex: 1 }} value={form.category} onChange={(e) => handleFormChange('category', e.target.value)}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
            </select>
            <input style={{ ...styles.input, flex: 1 }} type="number" placeholder="Max participants" value={form.max_participants} onChange={(e) => handleFormChange('max_participants', e.target.value)} />
          </div>

          <textarea style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} placeholder="Description courte" value={form.description} onChange={(e) => handleFormChange('description', e.target.value)} maxLength={500} />

          {createError && <div style={styles.errorText}>{createError}</div>}

          <div style={styles.row}>
            <button type="button" style={{ ...styles.submitBtn, backgroundColor: theme.bgInput, color: theme.textSecondary, flex: 1 }} onClick={() => { setShowCreateForm(false); setForm(emptyForm); }}>Annuler</button>
            <button type="submit" style={{ ...styles.submitBtn, flex: 1 }} disabled={creating}>{creating ? 'Creation...' : 'Publier'}</button>
          </div>
        </form>
      )}

      {toast && <div style={styles.toast}>{toast}</div>}

      {loading && <div style={styles.emptyState}>Chargement...</div>}
      {!loading && events.length === 0 && <div style={styles.emptyState}>Aucun evenement pour le moment. Sois le premier a en creer un !</div>}

      {!loading && pageEvents.map((event) => {
        const cat = CATEGORIES.find((c) => c.value === event.category) || CATEGORIES[CATEGORIES.length - 1];
        const isCreator = isAuthenticated && String(event.creator_id) === String(userId);
        const hasJoined = !!event.is_member;
        return (
          <div key={event.id} style={styles.eventCard} onClick={() => handleOpenEvent(event)}>
            <div style={styles.eventHeader}>
              <div style={styles.eventTitle}>{cat.icon} {event.title}</div>
              <span style={styles.badge(event.type === 'private')}>{event.type === 'private' ? 'Prive' : 'Public'}</span>
            </div>
            <div style={styles.eventMeta}>
              📍 {event.district ? `${event.district}, ` : ''}{event.city} · 📅 {event.event_date} {event.event_time || ''} · 👥 {event.participant_count}{event.max_participants ? `/${event.max_participants}` : ''}
            </div>
            {event.description && <div style={styles.eventDesc}>{event.description}</div>}
            {(hasJoined || isCreator) && <div style={styles.memberHint}>💬 Touche pour ouvrir le groupe</div>}

            <div style={styles.actionsRow} onClick={(e) => e.stopPropagation()}>
              {!hasJoined && !isCreator && (
                <button style={{ ...styles.actionBtn, backgroundColor: theme.gold, color: '#FFFFFF' }} onClick={() => handleJoin(event)}>
                  {isAuthenticated ? 'Rejoindre' : '🔒 Se connecter'}
                </button>
              )}
              {(hasJoined || isCreator) && (
                <>
                  <button style={{ ...styles.actionBtn, backgroundColor: theme.gold, color: '#FFFFFF' }} onClick={() => handleOpenEvent(event)}>💬 Ouvrir</button>
                  <button style={{ ...styles.actionBtn, backgroundColor: theme.bgInput, color: theme.textSecondary }} onClick={() => handleLeave(event)}>Quitter</button>
                </>
              )}
              <button style={{ ...styles.actionBtn, backgroundColor: theme.bgInput, color: theme.textSecondary }} onClick={() => handleShare(event)}>Partager</button>
              {isCreator && (
                <button style={{ ...styles.actionBtn, backgroundColor: '#F56565', color: '#FFFFFF' }} onClick={() => handleCancel(event)}>Annuler</button>
              )}
              {isAdmin && (
                <button style={{ ...styles.actionBtn, backgroundColor: '#F56565', color: '#FFFFFF' }} onClick={() => handleAdminDelete(event)}>🛡️ Supprimer (admin)</button>
              )}
            </div>
          </div>
        );
      })}

      {!loading && totalPages > 1 && (
        <div style={styles.pagination}>
          <button style={styles.pageArrow} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>← Prec.</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
            <button key={num} style={styles.pageBtn(num === currentPage)} onClick={() => setCurrentPage(num)}>{num}</button>
          ))}
          <button style={styles.pageArrow} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Suiv. →</button>
        </div>
      )}

      {openChatEvent && (
        <EventChatWindow
          eventId={openChatEvent.id}
          eventTitle={openChatEvent.title}
          userId={userId}
          onClose={() => setOpenChatEvent(null)}
        />
      )}
    </div>
  );
};

export default EventCard;